import { retrieveFactoryProductList } from '@/api/factory'
import { usePlugin } from '@/hooks/usePlugin'
import { useCurrencyStore } from '@/pinia/currency'
import { useLanguageStore } from '@/pinia/language'
import { buriedPointUtil, isMobile } from '@/utils/utils'
import { createSharedComposable, useEventListener } from '@vueuse/core'

/**
 * @returns {object}
 * filter - 返回过滤项配置
 * loading - 当前是否处于加载中
 * finished - 是否已加载完成，加载完成后不再触发 onLoad 事件
 * list - 返回列表数据
 * onLoad - 加载更多
 * onReset - 重置 page / loading / finished 状态并触发 onLoad 事件
 */
export const useProductList = createSharedComposable(() => {
  const loading = ref(false)
  const finished = ref(false)
  const currency = useCurrencyStore()
  const { $toast } = usePlugin()
  const route = useRoute()
  const language = useLanguageStore()

  const filter = reactive({
    sellerOpenId: route.params.id,
    keyword: route.query.keyword,
    language: language.current,
    sortMap: { monthSold: 'desc' },
    priceStart: null,
    priceEnd: null
  })
  const page = reactive({
    pageNow: 1,
    pageSize: 50
  })

  const list = shallowRef([])

  useEventListener(window, 'scroll', (e) => {
    if (loading.value || finished.value) return
    const el = e.target.scrollingElement
    if (el.scrollHeight - el.clientHeight - el.scrollTop < 75) {
      onLoad()
    }
  })

  /**
   * 防止请求竞态导致列表数据展示不正确问题
   */
  let latestRequestId = 0

  const onLoad = async () => {
    if (loading.value || finished.value) return
    try {
      const currentRequestId = ++latestRequestId
      const { priceStart, priceEnd, ...rest } = filter
      const data = { ...rest, ...page }
      if (priceStart) {
        data.priceStart = priceStart / currency.current.rate
      }
      if (priceEnd) {
        data.priceEnd = priceEnd / currency.current.rate
      }
      loading.value = true
      const rows = await getList(data)

      if (currentRequestId === latestRequestId) {
        if (rows.length !== 0) {
          list.value = [...list.value, ...rows]
          page.pageNow++
        } else {
          finished.value = true
        }
        /**
         * 不用 finally 修改 loading 状态是因为 当 请求竞态 出现时，finally 会导致 loading 状态不正确
         */
        loading.value = false
      }
    } catch (e) {
      $toast(e.message ?? 'Network Error, please try again.', { type: 'error' })
      console.error(e)
      loading.value = false
    }
  }

  const getList = async (filter) => {
    const { code, data, msg } = await retrieveFactoryProductList({
      ...filter
    }, {
      failedRedirect: filter.pageNow === 1
    })
    if (code !== 200) {
      throw new Error(msg)
    }

    if (filter.keyword) {
      buriedPointUtil({
        eventId: isMobile() ? 'HFL401' : 'PFL401',
        others: {
          keyword: filter.keyword
        }
      })
    }
    return data.rows
  }

  const onReset = () => {
    page.pageNow = 1
    list.value = []
    loading.value = false
    finished.value = false
    onLoad()
  }

  onBeforeMount(() => {
    onLoad()
  })

  const onRemoveItem = (offerId) => {
    list.value = list.value.filter(item => item.offerId !== offerId)
  }

  return { filter, loading, finished, list, onLoad, onReset, onRemoveItem }
})
